import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type CSSProperties,
} from "react";
import { EditorState } from "@tiptap/pm/state";
import { isEqual } from "@tldraw/utils";
import { generateHTML, type Extensions } from "@tiptap/core";
import { EditorContent, useEditor as useTipTap } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type { TLRichText, TLShapeId } from "@tldraw/tlschema";
import type { PlanroomEditor } from "../../editor";
import type { CanvasEditor } from "../editor";

// Rich text shown on shapes (text, labels on geo, notes and arrows) and
// edited in place with TipTap when its shape is being edited.

export const defaultExtensions: Extensions = [StarterKit];

export function richTextHtml(
  richText: TLRichText | undefined,
  extensions: Extensions = defaultExtensions,
) {
  if (!richText || typeof document === "undefined") return "";
  try {
    return generateHTML(richText as never, extensions);
  } catch {
    return "";
  }
}

export function isEmptyRichText(richText: TLRichText | undefined) {
  return !JSON.stringify(richText ?? {}).includes('"text":');
}

/**
 * Focus a rich-text editor once it is on screen. Shapes are created on
 * pointer-down, and the browser then focuses the canvas, so TipTap's own
 * autofocus would lose the caret.
 */
export function useFocusWhenMounted(tiptap: ReturnType<typeof useTipTap>) {
  useEffect(() => {
    if (!tiptap) return;
    // TipTap's own focus flag can stay set from autofocus; trust the DOM.
    const focus = () => {
      if (!tiptap.isDestroyed && document.activeElement !== tiptap.view.dom)
        tiptap.commands.focus("end");
    };
    focus();
    // Again after the browser's own pointer-down focus, for shapes created on press.
    const timer = setTimeout(focus);
    return () => clearTimeout(timer);
  }, [tiptap]);
}

export function RichTextLabel({
  editor,
  shapeId,
  type,
  richText,
  editing,
  extensions = defaultExtensions,
  style,
  className,
}: {
  editor: PlanroomEditor;
  shapeId: TLShapeId;
  type: string;
  richText: TLRichText;
  editing: boolean;
  extensions?: Extensions;
  style?: CSSProperties;
  className?: string;
}) {
  const html = useMemo(
    () => richTextHtml(richText, extensions),
    [richText, extensions],
  );
  return (
    <div
      className={["canvas-rich-label", className].filter(Boolean).join(" ")}
      style={style}
    >
      {editing ? (
        <RichTextEditor
          editor={editor}
          shapeId={shapeId}
          type={type}
          richText={richText}
          extensions={extensions}
        />
      ) : (
        <div
          className="canvas-rich-label-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  );
}

function RichTextEditor({
  editor,
  shapeId,
  type,
  richText,
  extensions,
}: {
  editor: PlanroomEditor;
  shapeId: TLShapeId;
  type: string;
  richText: TLRichText;
  extensions: Extensions;
}) {
  const accepted = useRef(richText);
  const tiptap = useTipTap({
    extensions,
    content: richText as never,
    autofocus: "end",
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (event.key === "Escape") {
          editor.cancel();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: rich }) => {
      const current = editor.getShape(shapeId);
      if (
        !current ||
        !("richText" in current.props) ||
        editor.getIsReadonly() ||
        editor.isShapeOrAncestorLocked(current)
      )
        return;
      // A remote replacement can arrive before React's reconciliation effect.
      // Do not publish the stale editor JSON over that newer board record.
      if (!isEqual(current.props.richText, accepted.current)) return;
      const next = rich.getJSON() as TLRichText;
      accepted.current = next;
      editor.updateShape({
        id: shapeId,
        type,
        props: { richText: next },
      } as never);
    },
  });
  useLayoutEffect(() => {
    if (!tiptap || tiptap.isDestroyed) return;
    const reconcile = () => {
      if (tiptap.isDestroyed || tiptap.view.composing) return;
      const shape = editor.getShape(shapeId);
      if (!shape || !("richText" in shape.props)) return;
      const current = shape.props.richText as TLRichText;
      accepted.current = current;
      if (isEqual(tiptap.getJSON(), current)) return;
      // An external replacement starts a fresh typing history. Mapping old
      // local typing steps through a full JSON replacement can erase remote text.
      tiptap.commands.setContent(current as never, { emitUpdate: false });
      const state = tiptap.state;
      tiptap.view.updateState(
        EditorState.create({
          schema: state.schema,
          doc: state.doc,
          selection: state.selection,
          plugins: state.plugins,
        }),
      );
      (editor as unknown as CanvasEditor).history.touch();
    };
    reconcile();
    const dom = tiptap.view.dom;
    const afterComposition = () => queueMicrotask(reconcile);
    dom.addEventListener("compositionend", afterComposition);
    return () => dom.removeEventListener("compositionend", afterComposition);
  }, [editor, shapeId, richText, tiptap]);
  useEffect(() => {
    const canvas = editor as unknown as CanvasEditor;
    canvas.setRichTextEditor?.(tiptap);
    const changed = () => canvas.history.touch();
    tiptap?.on("transaction", changed);
    return () => {
      tiptap?.off("transaction", changed);
      if (canvas.getRichTextEditor() === tiptap)
        canvas.setRichTextEditor?.(null);
    };
  }, [editor, tiptap]);
  useFocusWhenMounted(tiptap);
  return (
    <div data-canvas-interactive onPointerDown={(e) => e.stopPropagation()}>
      <EditorContent editor={tiptap} />
    </div>
  );
}
